import useAuthStore from './store/authStore';
import { auth, storage } from './lib/firebase';
import { signOut } from 'firebase/auth';
import { useEffect, useRef, useState, useMemo } from 'react';
import useChatStore from './store/chatStore';
import ChatMessage from './components/ChatMessage';
import ChatBox from './components/ChatBox';
import { formatMessageDate } from './lib/dateParse';
import Sidebar from './components/Sidebar';
import CustomContextMenu from './components/ContextMenu';
import { getDownloadURL, ref } from 'firebase/storage';

const ChatWindow = () => {
  const chatContainerRef = useRef(null);
  const scrollTarget = useRef();
  const sidebarRef = useRef(null);
  const chatWindowRef = useRef(null);
  const [replyToImageUrl, setReplyToImageUrl] = useState('');
  const [replyToVideoUrl, setReplyToVideoUrl] = useState('');

  const { setUser, userDetails, setSidebarStatus } = useAuthStore((state) => ({
    setUser: state.setUser,
    userDetails: state.userDetails,
    setSidebarStatus: state.setSidebarStatus,
  }));

  const {
    userChats,
    setUserChats,
    setQuickReaction,
    replyToMsg,
    setReplyToMsg,
  } = useChatStore((state) => ({
    userChats: state.userChats,
    setUserChats: state.setUserChats,
    setQuickReaction: state.setQuickReaction,
    replyToMsg: state.replyToMsg,
    setReplyToMsg: state.setReplyToMsg,
  }));

  const handleSidebar = (e) => {
    e.preventDefault();
    setSidebarStatus(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout failed', error.message);
    }
  };

  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [contextMenuOptions, setContextMenuOptions] = useState([]);

  const replyToWrapperRef = useRef(null); // Create a reference for the .replyto-wrapper
  const [replyToWrapperHeight, setReplyToWrapperHeight] = useState(0); // State to hold the height of the .replyto-wrapper

  const replyTo = (message) => {
    let replyPayload = {};

    replyPayload.msgId = message.id;

    if (message.text) {
      replyPayload.text = message.text.split(' ').slice(0, 100).join(' ');
    } else if (message.text === '') {
      if (message.imageName) {
        const fetchImage = async () => {
          try {
            const url = await getDownloadURL(
              ref(storage, 'uploads/' + message.imageName)
            );
            setReplyToImageUrl(url);
          } catch (error) {
            console.error('Error fetching image:', error);
          }
        };

        fetchImage();
        replyPayload.imageName = message.imageName;
        replyPayload.imageMetaData = message.imageMetaData;
      } else {
        const fetchVideo = async () => {
          try {
            const url = await getDownloadURL(
              ref(storage, 'uploads/' + message.videoName)
            );
            setReplyToVideoUrl(url);
          } catch (error) {
            console.error('Error fetching image:', error);
          }
        };

        fetchVideo();

        replyPayload.videoName = message.videoName;
        replyPayload.videoMetaData = message.videoMetaData;
      }
    }

    setReplyToMsg(replyPayload);
    setContextMenuVisible(false);
  };

  const handleContextMenu = (e, message) => {
    e.preventDefault();
    setContextMenuVisible(true);
    setContextMenuPosition({
      x: e.clientX,
      y: e.clientY,
    });

    setContextMenuOptions([
      {
        label: 'Reply',
        onClick: () => {
          replyTo(message);
        },
      },
    ]);
  };

  useEffect(() => {
    if (replyToWrapperRef.current) {
      setReplyToWrapperHeight(replyToWrapperRef.current.offsetHeight);
    }
  }, [replyToMsg]);

  useEffect(() => {
    setUserChats();
    scrollTarget.current.scrollTo();

    function handleClickOutside(event) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.classList.contains('profile-pic')
      ) {
        setSidebarStatus(false);
      }

      // Hide the context menu on outside click
      if (!event.target.closest('#context-menu')) {
        setContextMenuVisible(false);
      }
    }

    function handleScroll() {
      setContextMenuVisible(false);
    }

    document.body.addEventListener('click', handleClickOutside);
    chatContainerRef.current.addEventListener('scroll', handleScroll);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);

      if (chatContainerRef?.current) {
        chatContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [setUserChats, setSidebarStatus]);

  const sendHeartReaction = (m) => {
    setQuickReaction(auth.currentUser.uid, m);
  };

  const cancelReplyTo = () => {
    setReplyToMsg(null);
  };

  const greetingMessage = useMemo(() => {
    const greetings = [
      'Hello',
      'Hola',
      'Ciao',
      'Olá',
      'Merhaba',
      'नमस्ते',
      'હેલો',
      'Sat Sri Akal',
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  }, []);

  return (
    <div className="chat-window" ref={chatWindowRef}>
      <header>
        <div className="box">
          <div className="row middle-xs">
            <div className="col-xs-8">
              <a href="/#" className="profile-pic" onClick={handleSidebar}>
                <img src={userDetails && userDetails.avatar} alt="" />
              </a>{' '}
              <span>
                🙏🏼 {greetingMessage}, {userDetails && userDetails.displayName}
              </span>
            </div>
            <div className="col-xs-4 end-xs">
              <button
                onClick={handleSignOut}
                className="primary-btn primary-btn-inverse"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      <Sidebar passRef={sidebarRef} />
      <div className="message-window box">
        <section id="message-box" ref={chatContainerRef}>
          <div className="row">
            <div className="col-xs-12">
              {userDetails &&
                userChats &&
                userChats.map((i) => (
                  <div key={i.date} className="day-msgs">
                    <time className="day-format">
                      <span>{formatMessageDate(i.date)}</span>
                    </time>

                    {i.chats.map((m) => (
                      <ChatMessage
                        key={m.id}
                        payload={m}
                        sendHeartReaction={() => sendHeartReaction(m)}
                        contextMenu={(e) => handleContextMenu(e, m)}
                      />
                    ))}
                  </div>
                ))}
              <div ref={scrollTarget}></div>
            </div>
          </div>
          <CustomContextMenu
            visible={contextMenuVisible}
            menuPosition={contextMenuPosition}
            options={contextMenuOptions}
          />
        </section>
      </div>
      <div className="chatbox">
        <div className="box">
          {replyToMsg && (
            <div
              className="replyto-wrapper"
              ref={replyToWrapperRef}
              style={{ top: `-${replyToWrapperHeight}px` }}
            >
              {(() => {
                if (replyToMsg) {
                  if (replyToMsg.text) {
                    return replyToMsg.text;
                  } else if (replyToMsg.imageName) {
                    return (
                      <img
                        src={replyToImageUrl}
                        alt={replyToMsg.imageName}
                        onContextMenu={(e) => {
                          e.preventDefault();
                        }}
                      />
                    );
                  } else if (replyToMsg.videoName) {
                    return (
                      <video
                        src={replyToVideoUrl}
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => {
                          e.preventDefault();
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    );
                  }
                }
              })()}
              <span className="material-icons" onClick={cancelReplyTo}>
                close
              </span>
            </div>
          )}
          <ChatBox scrollTo={scrollTarget} />
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
